import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge, RelationPicker } from './ui';
import { RELATION_CONFIGS, relationLabel } from './relationConfigs';
import { unwrapLookupItems, toInt } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { useDistributorScope } from '../hooks/useDistributorScope';
import { formatTrPhoneInput, isCompleteTrPhone } from '../utils/phone';
import { digitsOnly, isExactDigits, sanitizeNumericInput } from '../utils/format';
import LocationPickerMap from './warehouse/LocationPickerMap';
import { COUNTRIES, getProvinces, getDistricts, getCountryCode, getCountryView, toOptions, resolveTrAddress } from '../utils/trGeo';
import { fetchBoolSetting } from './invoiceShared';
import { useFullRecordRefill } from './detail/useFullRecordRefill';
import { SETTING_CUSTOMER_KVKK_ENABLED } from '../utils/settings';
import {
  DetailShell, Section, Icon, SubModal, SplitButton, Grid,
  FieldText, FieldSelect, FieldCustom,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';
import { parseFieldOptions, fieldOptionLabel } from './additionalFieldOptions';

const API_BASE = '/api/customers';

// #129 Varsayılan Sevkiyat Belge Tipi seçenekleri (CustomersPage ile aynı).
const SHIPMENT_DOC_TYPES = [
  { value: '', label: 'Seçiniz' },
  { value: 'IRSALIYE', label: 'İrsaliye' },
  { value: 'FATURA', label: 'Fatura' },
];
// #129 E-Belge Mükellef Tipi.
const EDOCUMENT_TYPE_OPTIONS = [
  { value: '', label: 'Seçiniz' },
  { value: 'EINVOICE', label: 'E-Fatura' },
  { value: 'EINVOICE_EDISPATCH', label: 'E-Fatura + E-İrsaliye' },
  { value: 'EARCHIVE', label: 'E-Arşiv' },
];
// #140 Vergi Tipi (TaxType int). 0=Gerçek Kişi (TC aktif), 1=Tüzel, 2=Vergi Muafiyetli,
// 3=Yabancı. Gerçek Kişi dışında Vergi Kimlik No aktif olur. Default: Gerçek Kişi.
const TAX_TYPE_OPTIONS = [
  { value: '0', label: 'Gerçek Kişi (TC Kimlik)' },
  { value: '1', label: 'Tüzel Kişi (Vergi No)' },
  { value: '2', label: 'Vergi Muafiyetli' },
  { value: '3', label: 'Yabancı' },
];
// #140 Temel 5 para birimi. Değer = ISO kodu; CustomerCurrencyType (string) saklar.
const CURRENCY_OPTIONS = [
  { value: 'TL', label: '₺ TL — Türk Lirası' },
  { value: 'USD', label: '$ USD — Amerikan Doları' },
  { value: 'EUR', label: '€ EUR — Euro' },
  { value: 'GBP', label: '£ GBP — İngiliz Sterlini' },
  { value: 'CHF', label: '₣ CHF — İsviçre Frangı' },
];
// #140/#265 KVKK aydınlatma/açık rıza durumu artık salt-okunur bayrak (Onaylı/Onaysız);
// değer parametreye göre otomatik atanır, elle seçilmez.
// #140 Müşteri onay (segmentasyon) durumu.
const APPROVAL_OPTIONS = [
  { value: 'ONAYSIZ', label: 'Onaysız' },
  { value: 'BEKLEMEDE', label: 'Beklemede' },
  { value: 'ONAYLI', label: 'Onaylı' },
];
// #140 "Merkez" depo = "Ana Depo" türü (sistemde Merkez türü yok), "Tali Depo" = Tali türü.
// İkisi AYRI iki alan (Merkez=CustomerWarehouse, Tali=CustomerSubWarehouse); distribütöre bağlı filtrelenir.

// #240 Sezonluk çalışma — genel sezon tipi (yazlık/kışlık) + çalışma ayları.
// SeasonalInfo JSON info olarak saklanır: { seasonal, type, months:[1..12] }.
const SEASON_TYPE_OPTIONS = [
  { value: '', label: 'Seçiniz' },
  { value: 'YAZLIK', label: 'Yazlık' },
  { value: 'KISLIK', label: 'Kışlık' },
];
const MONTHS = [
  { value: 1, label: 'Ocak' }, { value: 2, label: 'Şubat' }, { value: 3, label: 'Mart' },
  { value: 4, label: 'Nisan' }, { value: 5, label: 'Mayıs' }, { value: 6, label: 'Haziran' },
  { value: 7, label: 'Temmuz' }, { value: 8, label: 'Ağustos' }, { value: 9, label: 'Eylül' },
  { value: 10, label: 'Ekim' }, { value: 11, label: 'Kasım' }, { value: 12, label: 'Aralık' },
];

// SeasonalInfo JSON'unu forma çözer (bozuk/boş → sezonluk kapalı).
function parseSeasonalInfo(raw) {
  const empty = { isSeasonal: false, seasonType: '', seasonMonths: [] };
  if (!raw) return empty;
  try {
    const o = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!o || typeof o !== 'object') return empty;
    return {
      isSeasonal: !!o.seasonal,
      seasonType: typeof o.type === 'string' ? o.type : '',
      seasonMonths: Array.isArray(o.months)
        ? o.months.map((m) => parseInt(m, 10)).filter((m) => m >= 1 && m <= 12)
        : [],
    };
  } catch { return empty; }
}

// #140/#163 TR telefon formatı paylaşılan utils/phone.js'e taşındı (formatTrPhone).
const STATUS_META = {
  0: { label: 'Aktif', tone: 'ok' },
  1: { label: 'Pasif', tone: 'neutral' },
  2: { label: 'Beklemede', tone: 'wait' },
  // 3 = Potansiyel: "İşlem Görmeyen Müşteriler" zamanlanmış görevinin (menü 9608) yazdığı durum.
  // Müşteri PASİF DEĞİLDİR — rutta kalır, sipariş alabilir; hareket görünce otomatik Aktif'e döner.
  3: { label: 'Potansiyel', tone: 'wait' },
};

// Potansiyel, düzenleme dropdown'ında BİLEREK YOK — sistem tarafından yönetilen bir durumdur.
//
// Görev müşteriyi Potansiyel yaparken "önceki durum + önceki rut günü"nü bir işaret satırına yazar
// ve satış gelince oradan GERİ SARAR. Elle Potansiyel seçilebilseydi o müşterinin işareti olmazdı;
// satış yapsa bile Aktif'e dönmez, sonsuza dek Potansiyel kalırdı. Rozet ve filtre yine çalışır —
// yalnız elle ATAMA kapalıdır.
const STATUS_OPTIONS = [
  { value: '', label: 'Seçiniz' },
  ...Object.entries(STATUS_META)
    .filter(([value]) => Number(value) !== 3)
    .map(([value, meta]) => ({ value, label: meta.label })),
];

// #140 Mantıksal sekmeler (diğer detay ekranlarıyla aynı DetailShell tab deseni).
const TABS = [
  { key: 'genel', label: 'Genel', icon: 'user' },
  { key: 'siniflandirma', label: 'Sınıflandırma', icon: 'layers' },
  { key: 'adres', label: 'Adres & Konum', icon: 'mapPin' },
  { key: 'finans', label: 'Vergi & Finans', icon: 'wallet' },
  { key: 'belge', label: 'Belge Ayarları', icon: 'list' },
  { key: 'ekbilgiler', label: 'Ek Bilgiler', icon: 'info' }, // #744 müşteri özelleştirilmiş alan değerleri
];

// Tüm form alanları. Backend CreateCustomerRequest / CustomerWriteRequest'in
// gerçekten kabul ettiği alanlardan oluşur (Region/District/Gold/Photo hariç).
const EMPTY_FORM = {
  // Müşteri Bilgileri
  textId: '', title: '', shortName: '', person: '',
  phone: '', cellPhone: '', email: '', fax: '', web: '',
  kvkkStatus: 'ONAYSIZ',
  // #240 Sezonluk çalışma (SeasonalInfo JSON info olarak saklanır)
  isSeasonal: false, seasonType: '', seasonMonths: [],
  // Sınıflandırma / Segmentasyon
  groupId: '', subGroupId: '', vendorId: '', salesRepresentativeId: '',
  status: 0, erpId: '', uniqueCode: '', specialCode: '', approvalStatus: 'ONAYSIZ',
  // Adres & Konum (#809 Ülke default TR — yeni müşteri Türkiye ile açılır, İl/İlçe kaskadı hazır)
  city: '', province: '', neighborhood: '', street: '',
  adress: '', adress2: '', apartmentNo: '', postCode: '', country: 'Türkiye',
  customerWarehouse: '', customerSubWarehouse: '', latitude: '', longtitude: '',
  // Vergi & Kimlik
  identityNumber: '', taxNumber: '', taxOffice: '', taxType: '0',
  // Finans
  creditLimit1: '', creditLimit2: '', dueDate: '',
  customerCurrencyType: 'TL', currentBalance: '',
  // Belge Ayarları
  defaultShipmentDocumentType: '', eDocumentTaxpayerType: '',
  isEinvoice: false, isEdispatch: false, isPublicInstitution: false,
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'data', 'Data', 'value', 'Value') ?? payload; }

// String alanları '' fallback; sayısal/Guid alanları olduğu gibi alıp string'e çevir.
function s(r, ...keys) { const v = pick(r, ...keys); return v === undefined || v === null ? '' : v; }
function n(r, ...keys) { const v = pick(r, ...keys); return v === undefined || v === null ? '' : String(v); }
function b(r, ...keys) { const v = pick(r, ...keys); return v === true || v === 'true' || v === 1; }

function rowToForm(r) {
  return {
    textId: s(r, 'textId', 'TextId'),
    title: s(r, 'title', 'Title'),
    shortName: s(r, 'shortName', 'ShortName'),
    person: s(r, 'person', 'Person'),
    phone: s(r, 'phone', 'Phone'),
    cellPhone: s(r, 'cellPhone', 'CellPhone'),
    email: s(r, 'email', 'Email'),
    fax: s(r, 'fax', 'Fax'),
    web: s(r, 'web', 'Web'),
    kvkkStatus: (s(r, 'kvkkStatus', 'KvkkStatus') || 'ONAYSIZ'),
    approvalStatus: (s(r, 'approvalStatus', 'ApprovalStatus') || 'ONAYSIZ'),
    ...parseSeasonalInfo(pick(r, 'seasonalInfo', 'SeasonalInfo')),
    groupId: s(r, 'groupId', 'GroupId'),
    subGroupId: s(r, 'subGroupId', 'SubGroupId'),
    vendorId: s(r, 'vendorId', 'VendorId'),
    salesRepresentativeId: s(r, 'salesRepresentativeId', 'SalesRepresentativeId', 'repId', 'RepId'),
    status: n(r, 'status', 'Status'),
    erpId: s(r, 'erpId', 'ErpId'),
    uniqueCode: s(r, 'uniqueCode', 'UniqueCode'),
    specialCode: s(r, 'specialCode', 'SpecialCode'),
    city: s(r, 'city', 'City'),
    province: s(r, 'province', 'Province'),
    neighborhood: s(r, 'neighborhood', 'Neighborhood'),
    street: s(r, 'street', 'Street'),
    adress: s(r, 'adress', 'Adress'),
    adress2: s(r, 'adress2', 'Adress2'),
    apartmentNo: s(r, 'apartmentNo', 'ApartmentNo'),
    postCode: s(r, 'postCode', 'PostCode'),
    // #809 Boş ülke = eski TR kayıt kabul edilir (kaskad çalışsın, İl aktif olsun). Kolon adı/verisi değişmedi.
    country: (s(r, 'country', 'Country') || 'Türkiye'),
    latitude: n(r, 'latitude', 'Latitude'),
    longtitude: n(r, 'longtitude', 'Longtitude'),
    identityNumber: s(r, 'identityNumber', 'IdentityNumber'),
    taxNumber: s(r, 'taxNumber', 'TaxNumber'),
    taxOffice: s(r, 'taxOffice', 'TaxOffice'),
    taxType: (n(r, 'taxType', 'TaxType') || '0'),
    creditLimit1: n(r, 'creditLimit1', 'CreditLimit1'),
    creditLimit2: n(r, 'creditLimit2', 'CreditLimit2'),
    dueDate: n(r, 'dueDate', 'DueDate'),
    customerCurrencyType: (s(r, 'customerCurrencyType', 'CustomerCurrencyType') || 'TL'),
    customerWarehouse: s(r, 'customerWarehouse', 'CustomerWarehouse'),
    customerSubWarehouse: s(r, 'customerSubWarehouse', 'CustomerSubWarehouse'),
    currentBalance: n(r, 'currentBalance', 'CurrentBalance'),
    defaultShipmentDocumentType: s(r, 'defaultShipmentDocumentType', 'DefaultShipmentDocumentType'),
    eDocumentTaxpayerType: s(r, 'eDocumentTaxpayerType', 'EDocumentTaxpayerType'),
    isEinvoice: b(r, 'isEinvoice', 'IsEinvoice'),
    isEdispatch: b(r, 'isEdispatch', 'IsEdispatch'),
    isPublicInstitution: b(r, 'isPublicInstitution', 'IsPublicInstitution'),
  };
}

// '' / null → undefined (boş bırakıldıysa gönderme), aksi halde number.
function num(v) {
  if (v === '' || v === null || v === undefined) return null;
  const x = Number(String(v).replace(',', '.'));
  return Number.isFinite(x) ? x : null;
}
function intOrNull(v) {
  if (v === '' || v === null || v === undefined) return null;
  const x = parseInt(v, 10);
  return Number.isFinite(x) ? x : null;
}

export function CustomerEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  // #270 Salt-okunur görüntüleme modu (/customers/:id/view) — tüm alanlar okunur, kaydetme yok.
  const isView = location.pathname.endsWith('/view');
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  // #271 Geri dönüş hedefi: genel bakıştan gelindiyse oraya, aksi halde listeye.
  const backTo = location.state?.from || '/customers';
  usePageMeta(isView ? 'Müşteri Bilgileri' : (isNew ? 'Yeni Müşteri' : 'Müşteri Düzenle'), 'Müşteri kartı bilgilerini yönetin.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  // #826 Distribütör tipi kullanıcının yetkili distribütörü (oto-seçim için). Seçenek listesi
  // aşağıdaki `vendors` (/api/vendors/Select) — o uç zaten veri yetkisiyle daraltılmış.
  const { lockedVendorId, scopeLoaded } = useDistributorScope();
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState('genel');
  const [audit, setAudit] = useState(null);

  const [groups, setGroups] = useState([]);
  const [subGroups, setSubGroups] = useState([]);
  const [vendors, setVendors] = useState([]);
  // #749 representatives/warehouses lookup'ları kaldırıldı — Satış Temsilcisi ve Depo alanları
  // müşteri kartından çıkarıldı (temsilci/rut üzerinden yönetilir).
  // #744 Müşteri özelleştirilmiş alan tanımları + değerleri (Ek Bilgiler sekmesi).
  const [custFieldDefs, setCustFieldDefs] = useState([]);
  const [custFieldValues, setCustFieldValues] = useState({}); // { additionalFieldId: value }
  // #265 KVKK onay süreci parametresi (Genel Parametreler). false → müşteri Onaylı doğar, maskeleme yok.
  const [kvkkEnabled, setKvkkEnabled] = useState(false);
  const [kvkkLoaded, setKvkkLoaded] = useState(false);
  // #266 Adres konumu harita seçici modalı.
  const [mapOpen, setMapOpen] = useState(false);

  // #750 Müşterinin ait olduğu ÖZELLEŞTİRİLMİŞ GRUP (havuz) üyelikleri — ürün kartındaki #731'in
  // müşteri karşılığı. Üyelik ayrı bir tablo değildir: grubun KOD kriterinde müşterinin satırı
  // tutulur, yani karttan yapılan atama ile grup ekranından eklenen kriter AYNI veriye yazar.
  const [memberGroupIds, setMemberGroupIds] = useState([]);
  const [allDynamicGroups, setAllDynamicGroups] = useState([]); // { id, textId, description }
  const [memberModalOpen, setMemberModalOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberDraft, setMemberDraft] = useState([]);
  const [memberSaving, setMemberSaving] = useState(false);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  // #796 Kullanıcı forma dokunduysa arka plandaki tam-kayıt tazelemesi onu ezmesin.
  const { markDirty, refillIfClean } = useFullRecordRefill();
  const set = (k, v) => { markDirty(); setForm((c) => ({ ...c, [k]: v })); };

  // #750 Havuz üyeliği — modal aç/kapa, taslak seçim, sunucuya uygula (ürün kartıyla aynı akış).
  const memberCodes = memberGroupIds
    .map((gid) => allDynamicGroups.find((g) => g.id === gid)?.textId)
    .filter(Boolean)
    .join(', ');
  const openMemberModal = () => { setMemberDraft(memberGroupIds); setMemberSearch(''); setMemberModalOpen(true); };
  const toggleMemberDraft = (gid) => setMemberDraft((cur) => (cur.includes(gid) ? cur.filter((x) => x !== gid) : [...cur, gid]));
  const applyMemberships = async () => {
    if (isNew) return;
    setMemberSaving(true);
    try {
      const res = await fetch('/api/customerdynamicgroups/SetCustomerMemberships', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ customerId: id, groupIds: memberDraft }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Üyelik güncellenemedi');
      setMemberGroupIds(memberDraft);
      setMemberModalOpen(false);
      showToast('success', 'Özelleştirilmiş grup üyelikleri güncellendi');
    } catch (e) {
      showToast('error', e.message || 'Üyelik güncellenirken hata oluştu');
    } finally {
      setMemberSaving(false);
    }
  };
  // #266 Haritadan seçilen konumun reverse-geocode adresini forma aktarır (Getir/Migros benzeri).
  // İl/İlçe/Ülke kanonik listeye eşlenir; boş çözümlenen alan mevcut değeri ezmez.
  // İl değişirse eşleşmeyen İlçe temizlenir (yeni ile ait olmayan ilçe kalmasın).
  const applyResolvedAddress = (raw) => {
    const a = resolveTrAddress(raw);
    setForm((c) => {
      const next = { ...c };
      // #809 Ülke değişirse (ör. TR→KKTC) eski İl/İlçe artık geçersiz — kaskadı sıfırla.
      const countryChanged = a.country && a.country !== c.country;
      if (a.country) next.country = a.country;
      if (a.city) {
        next.city = a.city;
        next.province = (countryChanged || a.city !== c.city) ? (a.province || '') : (a.province || c.province);
      } else if (countryChanged) {
        next.city = '';
        next.province = '';
      } else if (a.province) {
        next.province = a.province;
      }
      if (a.neighborhood) next.neighborhood = a.neighborhood;
      if (a.street) next.street = a.street;
      if (a.houseNo) next.apartmentNo = a.houseNo;
      if (a.postCode) next.postCode = a.postCode;
      if (a.fullAddress) next.adress = a.fullAddress;
      return next;
    });
    showToast('success', 'Adres bilgileri haritadan forma aktarıldı.');
  };

  // #240 Çalışma ayı çoklu-seçim toggle'ı.
  const toggleMonth = (m) => setForm((c) => {
    const cur = c.seasonMonths || [];
    return { ...c, seasonMonths: cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m] };
  });

  const getGroupLabel = useCallback((gid) => {
    const g = groups.find((x) => x.id === gid);
    return g ? (g.description ? `${g.textId} - ${g.description}` : (g.textId || '')) : '';
  }, [groups]);
  const getSubGroupLabel = useCallback((sid) => {
    const g = subGroups.find((x) => x.id === sid);
    return g ? (g.description ? `${g.textId} - ${g.description}` : (g.textId || '')) : '';
  }, [subGroups]);
  const getVendorLabel = useCallback((vid) => {
    const v = vendors.find((x) => x.id === vid);
    return v ? relationLabel(v) : '';
  }, [vendors]);
  // Lookup'lar (grup/ek grup/distribütör) — relation picker'lar ve etiket çözümü için.
  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/customergroups/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
      fetch('/api/customersubgroups/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
      fetch('/api/vendors/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
    ]).then(([g, sg, v]) => {
      setGroups(unwrapLookupItems(g));
      setSubGroups(unwrapLookupItems(sg));
      setVendors(unwrapLookupItems(v));
    }).catch(() => {});
    // #265 KVKK onay süreci parametresini oku.
    fetchBoolSetting(SETTING_CUSTOMER_KVKK_ENABLED, authHeaders(), false)
      .then(setKvkkEnabled)
      .finally(() => setKvkkLoaded(true));
    // #744 Müşteri özelleştirilmiş alan TANIMLARI (Ek Bilgiler sekmesi).
    fetch('/api/customeradditionalfields/Select', { headers })
      .then((r) => (r.ok ? r.json() : []))
      .then((b) => setCustFieldDefs(unwrapLookupItems(b)))
      .catch(() => {});
    // #750 Seçici için TÜM özelleştirilmiş grup tanımları (kapsam uçta uygulanır).
    fetch('/api/customerdynamicgroups/Select', { headers })
      .then((r) => (r.ok ? r.json() : []))
      .then((b) => setAllDynamicGroups((unwrapLookupItems(b) || []).map((g) => ({
        id: g.id ?? g.Id,
        textId: g.textId ?? g.TextId ?? '',
        description: g.description ?? g.Description ?? '',
      }))))
      .catch(() => {});
  }, []);

  // #750 Bu müşterinin mevcut havuz üyelikleri (yeni müşteride yok — önce kayıt gerekir).
  useEffect(() => {
    if (isNew || !id) return;
    fetch(`/api/customerdynamicgroups/ForCustomer?customerId=${encodeURIComponent(id)}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => {
        const rows = b?.data ?? b?.Data ?? [];
        setMemberGroupIds(Array.isArray(rows) ? rows.map((g) => g.id ?? g.Id) : []);
      })
      .catch(() => {});
  }, [id, isNew]);

  // #744 Mevcut müşterinin özelleştirilmiş alan DEĞERLERİ (yeni müşteride yok — önce kayıt gerekir).
  useEffect(() => {
    if (isNew || !id) return;
    fetch(`/api/customeradditionalfields/ValuesForCustomer?customerId=${encodeURIComponent(id)}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((b) => {
        const map = {};
        (unwrapLookupItems(b) || []).forEach((it) => { map[it.additionalFieldId ?? it.AdditionalFieldId] = it.value ?? it.Value ?? ''; });
        setCustFieldValues(map);
      })
      .catch(() => {});
  }, [id, isNew]);

  // #749 Depo helper'ları ve oto-seçim efekti kaldırıldı (Merkez/Tali Depo alanları karttan çıktı).
  // Mevcut kayıtların customerWarehouse/customerSubWarehouse değerleri buildPayload'da korunur.

  // Tek kayıt yükleme: önce navigation state'ten gelen row; yoksa OData filtre ile id'den bul.
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    const stateRow = location.state?.row;
    if (stateRow && String(pick(stateRow, 'id', 'Id')) === String(id)) {
      setForm(rowToForm(stateRow));
      // #796 Nav-state satırı liste OData $select'inden gelir → YALNIZ GÖRÜNÜR KOLONLAR. Onay Durumu,
      // Adres bloğunun tamamı ve Vergi bloğunun tamamı liste projeksiyonunda olmadığı için müşteri
      // Düzenle ile açıldığında bu alanlar BOŞ görünüyordu (kayıt DB'de doluydu — İzle ekranından
      // Düzenle'ye geçince dolu gelmesinin sebebi buydu: o yol tam kaydı taşıyor).
      //
      // Tam kayıt zaten çekiliyordu ama YALNIZ audit için kullanılıyordu; artık forma da yazılıyor
      // (kullanıcı bu arada bir alana dokunduysa refillIfClean tazelemeyi atlar).
      fetch(`${API_BASE}/GetById?Id=${encodeURIComponent(id)}`, { headers: authHeaders() })
        .then((res) => (res.ok ? res.json() : null))
        .then((body) => {
          const rec = unwrap(body);
          if (!rec) return;
          refillIfClean(() => setForm(rowToForm(rec)));
          setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
        })
        .catch(() => {});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/GetById?Id=${encodeURIComponent(id)}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Kayıt alınamadı (${res.status})`);
      const body = await res.json();
      const r = unwrap(body);
      if (!r) throw new Error('Kayıt bulunamadı');
      setForm(rowToForm(r));
      setAudit({ createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(r, 'createdDate', 'CreatedDate'), updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    } catch (err) {
      showToast('error', err.message || 'Kayıt alınamadı');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  // #826 Distribütör tipi kullanıcı: yetkili distribütörü otomatik seçili gelsin.
  // 823/827/828/829'dan FARKI: 826 alanı KİLİTLEMEZ — kullanıcı yetkili olduğu başka bir
  // distribütöre (veya boşa) çekebilir. Bu yüzden yalnız alan BOŞKEN doldurulur; kaydın
  // kendi distribütörü ve kullanıcının elle yaptığı seçim ezilmez.
  useEffect(() => {
    if (!scopeLoaded || loading || !lockedVendorId) return;
    setForm((c) => (c.vendorId ? c : { ...c, vendorId: lockedVendorId }));
  }, [scopeLoaded, loading, lockedVendorId]);

  // #265 Yeni müşteride KVKK durumu parametreye göre doğar: süreç açıksa Onaysız, kapalıysa Onaylı.
  // (KVKK Durumu artık salt-okunur bilgi bayrağı; elle seçilmez.)
  useEffect(() => {
    if (isNew && kvkkLoaded) set('kvkkStatus', kvkkEnabled ? 'ONAYSIZ' : 'ONAYLI');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, kvkkLoaded, kvkkEnabled]);

  // Klonla: /customers/new'e kopya veriyle gelindiyse formu doldur (kayıt OLUŞTURMA).
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm({ ...EMPTY_FORM, ...location.state.clone });
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Backend'in (CreateCustomerRequest / CustomerWriteRequest) kabul ettiği alanlar.
  const buildPayload = () => ({
    textId: form.textId.trim(),
    title: form.title.trim(),
    shortName: form.shortName || null,
    person: form.person || null,
    phone: form.phone || null,
    cellPhone: form.cellPhone || null,
    email: form.email || null,
    fax: form.fax || null,
    web: form.web || null,
    groupId: form.groupId || null,
    subGroupId: form.subGroupId || null,
    vendorId: form.vendorId || null,
    salesRepresentativeId: form.salesRepresentativeId || null,
    status: toInt(form.status),
    erpId: form.erpId || null,
    uniqueCode: form.uniqueCode || null,
    specialCode: form.specialCode || null,
    city: form.city || null,
    province: form.province || null,
    neighborhood: form.neighborhood || null,
    street: form.street || null,
    adress: form.adress || null,
    adress2: form.adress2 || null,
    apartmentNo: form.apartmentNo || null,
    postCode: form.postCode || null,
    country: form.country || null,
    latitude: num(form.latitude),
    longtitude: num(form.longtitude),
    identityNumber: form.identityNumber || null,
    taxNumber: form.taxNumber || null,
    taxOffice: form.taxOffice || null,
    taxType: intOrNull(form.taxType),
    creditLimit1: num(form.creditLimit1),
    creditLimit2: num(form.creditLimit2),
    dueDate: intOrNull(form.dueDate),
    customerCurrencyType: form.customerCurrencyType || null,
    customerWarehouse: form.customerWarehouse || null,
    customerSubWarehouse: form.customerSubWarehouse || null,
    currentBalance: num(form.currentBalance),
    defaultShipmentDocumentType: form.defaultShipmentDocumentType || null,
    eDocumentTaxpayerType: form.eDocumentTaxpayerType || null,
    // #269 E-Fatura/E-İrsaliye mükellefiyeti artık tek combobox'tan (E-Belge Mükellef Tipi) türetilir;
    // ayrı kutucuklar kaldırıldı. Backend boolean kolonları tutarlı kalsın diye burada eşlenir.
    isEinvoice: form.eDocumentTaxpayerType === 'EINVOICE' || form.eDocumentTaxpayerType === 'EINVOICE_EDISPATCH',
    isEdispatch: form.eDocumentTaxpayerType === 'EINVOICE_EDISPATCH',
    isPublicInstitution: !!form.isPublicInstitution,
    kvkkStatus: form.kvkkStatus || 'ONAYSIZ',
    approvalStatus: form.approvalStatus || 'ONAYSIZ',
    // #240 Sezonluk info: sezonluk kapalıysa null (temizle), açıksa JSON blob.
    seasonalInfo: form.isSeasonal
      ? JSON.stringify({
          seasonal: true,
          type: form.seasonType || null,
          months: [...(form.seasonMonths || [])].sort((a, b) => a - b),
        })
      : null,
  });

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp boş yeni kayda hazırlan.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    // #748 Müşteri zorunlu alanları. Backend Add/Update Excel içe-aktarım ve başka akışlarla ORTAK
    // olduğundan oraya sıkı Required konulmaz; zorunluluk müşteri kartı formunda uygulanır.
    const req = [];
    if (!form.textId.trim()) req.push(['genel', 'Müşteri Kodu']);
    if (!form.title.trim()) req.push(['genel', 'Ünvan']);
    if (!String(form.shortName).trim()) req.push(['genel', 'Kısa Ad']);
    if (!String(form.person).trim()) req.push(['genel', 'İlgili Kişi']);
    if (!form.groupId) req.push(['siniflandirma', 'Müşteri Grubu']);
    if (!form.subGroupId) req.push(['siniflandirma', 'Ek Grup']);
    if (!form.vendorId) req.push(['siniflandirma', 'Distribütör']);
    if (!form.country) req.push(['adres', 'Ülke']);
    if (!form.city) req.push(['adres', 'İl']);
    if (!form.province) req.push(['adres', 'İlçe']);
    if (!String(form.adress).trim()) req.push(['adres', 'Adres']);
    if (String(form.taxType) === '') req.push(['finans', 'Vergi Tipi']);
    else if (String(form.taxType) === '0') {
      if (!String(form.identityNumber).trim()) req.push(['finans', 'TC Kimlik No']);
    } else {
      if (!String(form.taxNumber).trim()) req.push(['finans', 'Vergi Kimlik No']);
      if (!String(form.taxOffice).trim()) req.push(['finans', 'Vergi Dairesi']);
    }
    if (req.length) {
      setActiveTab(req[0][0]); // ilk eksik alanın sekmesine geç
      showToast('error', `Zorunlu alan(lar) eksik: ${req.map((x) => x[1]).join(', ')}`);
      return;
    }
    // Talep 748 (2. tur) — BİÇİM kontrolleri. Zorunluluk yukarıda ayrı kontrol edilir;
    // burada "doldurulduysa tam olmalı" kuralı işler. Eskiden eksik telefon (ör. yalnız
    // alan kodu) veya kısa TC sessizce kaydediliyordu.
    // Vergi tipine göre YALNIZ aktif olan alan denetlenir: pasif alanda duran eski/bozuk
    // veri (ör. 9 haneli legacy TC) ilgisiz bir düzenlemeyi engellememelidir.
    const fmt = [];
    const gercekKisi = String(form.taxType) === '0'; // yukarıdaki zorunluluk bloğuyla aynı ölçüt
    if (gercekKisi && !isExactDigits(form.identityNumber, 11)) fmt.push(['finans', 'TC Kimlik No 11 haneli olmalı']);
    if (!gercekKisi && !isExactDigits(form.taxNumber, 10)) fmt.push(['finans', 'Vergi Kimlik No 10 haneli olmalı']);
    if (!isCompleteTrPhone(form.phone)) fmt.push(['genel', 'Telefon (Sabit) eksik — alan kodu dahil en az 10 hane girin veya alanı boş bırakın']);
    if (!isCompleteTrPhone(form.cellPhone)) fmt.push(['genel', 'Cep Telefonu eksik — alan kodu dahil en az 10 hane girin veya alanı boş bırakın']);
    if (fmt.length) {
      setActiveTab(fmt[0][0]);
      showToast('error', fmt.map((x) => x[1]).join(' · '));
      return;
    }
    // #751 "Zorunlu" işaretli özelleştirilmiş alanlar — yalnız düzenlemede uygulanır (yeni müşteride
    // Ek Bilgiler sekmesi ancak kayıttan sonra doldurulabildiği için). Boşsa kaydı engelle.
    if (!isNew && custFieldDefs.length) {
      const emptyReq = custFieldDefs.filter((f) => {
        if (!pick(f, 'isRequired', 'IsRequired')) return false;
        const fid = pick(f, 'id', 'Id');
        return !String(custFieldValues[fid] ?? '').trim();
      });
      if (emptyReq.length) {
        setActiveTab('ekbilgiler');
        showToast('error', `Zorunlu özel alan(lar) boş: ${emptyReq.map((f) => pick(f, 'name', 'Name')).join(', ')}`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      const res = await fetch(`${API_BASE}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(isNew ? payload : { id, ...payload }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'message', 'Message') || (isNew ? 'Kayıt eklenemedi' : 'Kayıt güncellenemedi'));
      if (isNew) {
        if (andNew) {
          showToast('success', 'Müşteri eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Müşteri başarıyla eklendi');
        setTimeout(() => navigate('/customers'), 400);
        return;
      }
      // #744 Özelleştirilmiş alan değerlerini kaydet (yalnız düzenlemede — mevcut müşteri id'si var).
      if (custFieldDefs.length) {
        await fetch('/api/customeradditionalfields/SaveValues', {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({
            customerId: id,
            values: custFieldDefs.map((f) => {
              const fid = pick(f, 'id', 'Id');
              return { additionalFieldId: fid, value: String(custFieldValues[fid] ?? '') };
            }),
          }),
        }).catch(() => {}); // değer kaydı hatası müşteri kaydını kırmaz
      }
      showToast('success', 'Müşteri başarıyla güncellendi');
      // #271 Düzenleme genel bakıştan açıldıysa oraya dön, aksi halde listeye.
      setTimeout(() => navigate(backTo), 400);
    } catch (err) {
      showToast('error', err.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut kaydın form bilgilerini taşıyarak yeni-kayıt ekranını aç. Hiçbir şey kaydedilmez.
  const handleClone = () => {
    navigate('/customers/new', { state: { clone: { ...form, textId: '', currentBalance: '' } } });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id }) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'message', 'Message') || 'Kayıt silinemedi');
      showToast('success', 'Müşteri başarıyla silindi');
      setTimeout(() => navigate('/customers'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme işlemi sırasında hata oluştu');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const statusMeta = STATUS_META[Number(form.status)];
  const statusBadge = !isNew && statusMeta && <StatusBadge tone={statusMeta.tone}>{statusMeta.label}</StatusBadge>;

  // #140/#265 KVKK onaysız → hassas alanları maskele. Yalnız KVKK süreci AÇIKKEN ve MEVCUT
  // (yeni değil) kayıtta uygulanır: yeni müşteride veriyi kullanıcı girer (maskeleme bloklamamalı),
  // süreç kapalıyken maskeleme hiç uygulanmaz. Vergi tipine göre TC/Vergi No aktiflik.
  const kvkkMasked = kvkkEnabled && !isNew && form.kvkkStatus !== 'ONAYLI';
  const isGercekKisi = String(form.taxType) === '0';
  // Para birimi: kayıtlı (eski) değer listede yoksa koru.
  const currencyOptions = CURRENCY_OPTIONS.some((o) => o.value === form.customerCurrencyType) || !form.customerCurrencyType
    ? CURRENCY_OPTIONS
    : [{ value: form.customerCurrencyType, label: `${form.customerCurrencyType} (mevcut)` }, ...CURRENCY_OPTIONS];

  // #270 Görüntüleme modu: yalnız "Düzenle" (aynı origin'e dönecek) + "Kapat".
  const actions = isView ? (
    <>
      <button className="dps-btn ghost" onClick={() => navigate(backTo)}>Kapat</button>
      <button className="dps-btn primary" onClick={() => navigate(`/customers/${id}/edit`, { state: { row: form, from: backTo } })}>
        Düzenle
      </button>
    </>
  ) : (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate(backTo)} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni müşteri aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  // E-belge / kamu kurumu boolean kontrolleri (FieldCustom içinde checkbox).
  const checkRow = (k, label) => (
    <FieldCustom label={label}>
      <label className="dps-ff-input" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', border: 'none', background: 'transparent', padding: '6px 0' }}>
        <input type="checkbox" checked={!!form[k]} onChange={(e) => set(k, e.target.checked)} />
        <span>{form[k] ? 'Evet' : 'Hayır'}</span>
      </label>
    </FieldCustom>
  );

  return (
    <DetailShell
      readOnly={isView}
      crumb={[
        { label: 'Müşteriler', to: '/customers' },
        { label: isNew ? 'Yeni Müşteri' : (form.title || (isView ? 'Müşteri Bilgileri' : 'Müşteri Düzenle')), current: true },
      ]}
      status={statusBadge}
      tabs={TABS}
      activeTab={activeTab}
      onTab={setActiveTab}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'Customer', entityId: id }}
      loading={loading}
    >
      <div className="dps-form">
        {activeTab === 'genel' && (
        <Section icon="user" title="Müşteri Bilgileri" subtitle="Kimlik ve iletişim bilgileri">
          <Grid cols={2}>
            <FieldText form={form} set={set} tried={tried} k="textId" label="Müşteri Kodu" req mono />
            <FieldText form={form} set={set} tried={tried} k="title" label="Ünvan" req />
            <FieldText form={form} set={set} tried={tried} k="shortName" label="Kısa Ad (Tabela)" req />
            <FieldText form={form} set={set} tried={tried} k="person" label="İlgili Kişi (Yetkili)" req mask={kvkkMasked} />
            {/* Talep 748 (2. tur): formatTrPhoneInput, ayıraç silme tuzağına düşmez → alan
                kodu artık silinebilir (bkz. utils/phone.js withSeparatorDelete). */}
            <FieldText form={form} set={set} k="phone" label="Telefon (Sabit)" placeholder="(0212) 222 22 22" mask={kvkkMasked} format={formatTrPhoneInput} hint="Alan kodu dahil 11 hane" />
            <FieldText form={form} set={set} k="cellPhone" label="Cep Telefonu" placeholder="(0532) 222 22 22" format={formatTrPhoneInput} hint="Alan kodu dahil 11 hane" />
            <FieldText form={form} set={set} k="email" label="E-posta" type="email" mask={kvkkMasked} />
            <FieldText form={form} set={set} k="fax" label="Faks" placeholder="(0212) 222 22 22" format={formatTrPhoneInput} />
            <FieldText form={form} set={set} k="web" label="Web Sitesi" span />
          </Grid>
          {/* #140 KVKK + #240 Sezonluk müşteri — aynı kompakt satırda; sezonluk solda, KVKK sağda. */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 16, marginTop: 14 }}>
            {/* #240 Sezonluk müşteri — tasarım uyumlu seçim kutusu; işaretlenince sezon tipi + çalışma ayları açılır. */}
            <label className={'dps-check' + (form.isSeasonal ? ' is-on' : '')}>
              <input type="checkbox" checked={!!form.isSeasonal} onChange={(e) => set('isSeasonal', e.target.checked)} />
              <span className="dps-check-tt">
                <b>Sezonluk müşteri</b>
                <span>Yalnız belirli sezon/aylarda çalışır (bilgi amaçlı saklanır)</span>
              </span>
            </label>

            {/* #265 KVKK durumu — salt-okunur bilgi bayrağı (elle seçilmez).
                Parametre kapalıysa müşteri Onaylı doğar; açıksa Onaysız doğar, onay verilince Onaylı'ya geçer. */}
            <div className="dps-kvkk" style={{ marginTop: 0 }}>
              <span className="dps-kvkk-lbl">KVKK Durumu</span>
              <span className={'dps-kvkk-hint' + (form.kvkkStatus === 'ONAYLI' ? ' is-ok' : ' is-masked')}>
                <Icon name="shield" size={13} />
                {form.kvkkStatus === 'ONAYLI' ? 'Onaylı' : 'Onaysız'}
              </span>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--dps-ink-3)' }}>
                {!kvkkEnabled
                  ? 'KVKK onay süreci kapalı — tüm alanlar açık'
                  : (form.kvkkStatus === 'ONAYLI'
                      ? 'Hassas alanlar görünür'
                      : 'Onay verilene kadar hassas alanlar maskeli')}
              </span>
            </div>
          </div>

          {/* Sezonluk detay — işaretlenince sezon tipi + çalışma ayları (satırın altında tam genişlik). */}
          {form.isSeasonal && (
            <div style={{ marginTop: 14 }}>
              <Grid cols={2}>
                <FieldSelect form={form} set={set} k="seasonType" label="Sezon Tipi (Genel)" options={SEASON_TYPE_OPTIONS} />
              </Grid>
              <div style={{ marginTop: 14 }}>
                <div className="dps-ff-lbl" style={{ marginBottom: 8 }}>Çalışma Ayları</div>
                <div className="dps-month-chips">
                  {MONTHS.map((m) => {
                    const on = (form.seasonMonths || []).includes(m.value);
                    return (
                      <button key={m.value} type="button" onClick={() => toggleMonth(m.value)}
                        className={'dps-month-chip' + (on ? ' is-on' : '')}>
                        {m.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </Section>
        )}

        {activeTab === 'siniflandirma' && (
        <Section icon="layers" title="Sınıflandırma & Segmentasyon" subtitle="Grup, distribütör, satış temsilcisi, durum ve onay">
          <Grid cols={2}>
            <FieldCustom label="Müşteri Grubu" req invalid={tried && !form.groupId}>
              <RelationPicker config={RELATION_CONFIGS.customerGroup} rows={groups}
                value={form.groupId} selectedLabel={getGroupLabel(form.groupId)}
                onChange={(val) => set('groupId', val)} />
            </FieldCustom>
            <FieldCustom label="Ek Grup" req invalid={tried && !form.subGroupId}>
              <RelationPicker config={RELATION_CONFIGS.customerSubGroup} rows={subGroups}
                value={form.subGroupId} selectedLabel={getSubGroupLabel(form.subGroupId)}
                onChange={(val) => set('subGroupId', val)} />
            </FieldCustom>
            {/* #750 Müşterinin ait olduğu ÖZELLEŞTİRİLMİŞ GRUP havuzları (çoklu) — ürün kartındaki
                #731 alanının karşılığı. Kodlar virgülle listelenir; tıklanınca listeden seçilir.
                Yeni müşteride kapalı: üyelik satırı müşteri Id'sine yazılır, önce kayıt gerekir. */}
            <FieldCustom label="Ait Olduğu Özelleştirilmiş Gruplar (Havuzlar)" span>
              <button
                type="button"
                onClick={isNew ? undefined : openMemberModal}
                disabled={isNew}
                style={{
                  width: '100%', textAlign: 'left', padding: '9px 11px', borderRadius: 8,
                  border: '1px solid var(--dps-line, #d7dde5)', background: 'var(--dps-field-bg, #fff)',
                  fontSize: 13.5, cursor: isNew ? 'not-allowed' : 'pointer',
                  color: memberCodes ? 'var(--dps-ink, #1e293b)' : 'var(--dps-ink-3, #94a3b8)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, minHeight: 40,
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {isNew ? 'Önce müşteriyi kaydedin' : (memberCodes || 'Havuz seçmek için tıklayın')}
                </span>
                <Icon name="layers" size={15} />
              </button>
            </FieldCustom>
            {/* #826 Seçenekler /api/vendors/Select'ten gelir (rows={vendors}) → yalnız kullanıcının
                yetkili olduğu distribütörler; uç CustomerScopeFilter ile daraltır, burada tekrar
                filtrelenmez. Distribütör tipi kullanıcıda alan otomatik seçili gelir (oto-seçim
                efekti) ama KİLİTLENMEZ — 823/827/828/829'dan farkı budur. */}
            <FieldCustom label="Distribütör" req invalid={tried && !form.vendorId}>
              <RelationPicker config={RELATION_CONFIGS.vendor} rows={vendors}
                value={form.vendorId} selectedLabel={getVendorLabel(form.vendorId)}
                onChange={(val) => set('vendorId', val)} />
            </FieldCustom>
            {/* #749 "Satış Temsilcisi" müşteri kartından kaldırıldı — temsilci ilişkisi rut üzerinden
                yönetilir. Mevcut kayıtların salesRepresentativeId değeri buildPayload'da korunur (veri
                kaybı olmaz), yalnız kart üzerinde artık düzenlenmez. */}
            <FieldSelect form={form} set={set} k="status" label="Durum" options={STATUS_OPTIONS} />
            <FieldSelect form={form} set={set} k="approvalStatus" label="Onay Durumu" options={APPROVAL_OPTIONS} />
            <FieldText form={form} set={set} k="erpId" label="ERP Kodu" mono />
            <FieldText form={form} set={set} k="uniqueCode" label="Tekil Kod" mono />
            <FieldText form={form} set={set} k="specialCode" label="Özel Kod" mono />
          </Grid>
        </Section>
        )}

        {activeTab === 'adres' && (
        <Section icon="mapPin" title="Adres & Konum" subtitle="Coğrafi konum, adres ve depo bilgileri">
          <Grid cols={2}>
            {/* #809 Ülke → İl → İlçe kaskadı. Ülke koduna göre İl listesi değişir (TR≠KKTC).
                Ülke değişince İl+İlçe temizlenir; İl değişince İlçe temizlenir. */}
            <FieldSelect form={form} set={set} tried={tried} req k="country" label="Ülke" options={toOptions(COUNTRIES, form.country)}
              onChange={(v) => setForm((c) => ({ ...c, country: v, city: '', province: '' }))} />
            {/* #809 İl: seçili ÜLKEYE bağlı kanonik il listesinden (TR 81 il / KKTC 6 il).
                Ülke seçili değilse pasif. Eski serbest-metin değeri korunur. İl değişince İlçe sıfırlanır. */}
            <FieldSelect form={form} set={set} tried={tried} req k="city" label="İl" disabled={!form.country}
              options={toOptions(getProvinces(form.country), form.city)}
              onChange={(v) => setForm((c) => ({ ...c, city: v, province: '' }))} />
            {/* #809 İlçe: seçili ülke+il'e bağlı kanonik ilçe listesinden. İl seçili değilse pasif. */}
            <FieldSelect form={form} set={set} tried={tried} req k="province" label="İlçe" disabled={!form.city}
              options={toOptions(getDistricts(form.country, form.city), form.province)} />
            <FieldText form={form} set={set} k="neighborhood" label="Mahalle" />
            <FieldText form={form} set={set} k="street" label="Sokak" />
            <FieldText form={form} set={set} tried={tried} req k="adress" label="Adres" span />
            <FieldText form={form} set={set} k="adress2" label="Adres (devam)" span />
            <FieldText form={form} set={set} k="apartmentNo" label="Kapı No" />
            <FieldText form={form} set={set} k="postCode" label="Posta Kodu" />
            {/* #749 "Merkez Depo (Ana)" ve "Tali Depo" müşteri kartından kaldırıldı — depo eşleşmesi
                temsilci/rut parametreleri üzerinden yönetilir. Mevcut kayıtların değeri buildPayload'da
                korunur, yalnız kart üzerinde düzenlenmez. */}
            {/* #266 Adres konumunu haritadan pinle — kullanıcı dostu konum seçimi. */}
            <FieldCustom label="Konum (Harita)" span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <button type="button" className="dps-btn ghost" onClick={() => setMapOpen(true)}>
                  <Icon name="mapPin" size={16} /> Haritadan Konum Seç
                </button>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--dps-ink-3)', fontVariantNumeric: 'tabular-nums' }}>
                  {Number.isFinite(Number(form.latitude)) && form.latitude !== '' && Number.isFinite(Number(form.longtitude)) && form.longtitude !== ''
                    ? `📍 ${Number(form.latitude).toFixed(6)}, ${Number(form.longtitude).toFixed(6)}`
                    : 'Konum seçilmedi'}
                </span>
              </div>
            </FieldCustom>
            <FieldText form={form} set={set} k="latitude" label="Enlem (Lat)" num />
            <FieldText form={form} set={set} k="longtitude" label="Boylam (Lng)" num />
          </Grid>
        </Section>
        )}

        {/* #266 Harita konum seçici modalı — pini sürükle/tıkla veya adres ara. */}
        {mapOpen && (
        <SubModal title="Konum Seç" maxWidth={720} onClose={() => setMapOpen(false)}
          footer={<button className="dps-btn primary" onClick={() => setMapOpen(false)}>Tamam</button>}>
          <div style={{ fontSize: 12.5, color: 'var(--dps-ink-3)', marginBottom: 8 }}>
            Haritaya tıklayın veya pini sürükleyin; İl/İlçe/Mahalle/Sokak ve adres bilgileri otomatik doldurulur.
          </div>
          <LocationPickerMap
            lat={form.latitude !== '' && form.latitude != null ? Number(form.latitude) : undefined}
            lng={form.longtitude !== '' && form.longtitude != null ? Number(form.longtitude) : undefined}
            onChange={(la, ln) => setForm((c) => ({ ...c, latitude: la ?? '', longtitude: ln ?? '' }))}
            onAddress={applyResolvedAddress}
            countryCode={getCountryCode(form.country)}
            countryCenter={getCountryView(form.country).center}
            countryZoom={getCountryView(form.country).zoom}
          />
        </SubModal>
        )}

        {activeTab === 'finans' && (
        <>
        <Section icon="list" title="Vergi & Kimlik" subtitle="Vergi tipine göre TC veya Vergi Kimlik No aktif olur">
          <Grid cols={2}>
            <FieldSelect form={form} set={set} tried={tried} req k="taxType" label="Vergi Tipi" options={TAX_TYPE_OPTIONS} />
            <FieldText form={form} set={set} tried={tried} req={!isGercekKisi} k="taxOffice" label="Vergi Dairesi" />
            {/* Talep 748 (2. tur): TC Kimlik No yalnız RAKAM, en fazla 11 hane.
                Eskiden harf de girilebiliyor ve uzunluk sınırı yoktu. */}
            <FieldText form={form} set={set} tried={tried} req={isGercekKisi} k="identityNumber" label="TC Kimlik No"
              mask={kvkkMasked} readOnly={!isGercekKisi} format={digitsOnly(11)}
              hint={isGercekKisi ? '11 haneli, yalnız rakam' : undefined}
              placeholder={isGercekKisi ? '' : 'Gerçek Kişi seçilince aktif'} />
            <FieldText form={form} set={set} tried={tried} req={!isGercekKisi} k="taxNumber" label="Vergi Kimlik No"
              readOnly={isGercekKisi} format={digitsOnly(10)}
              hint={!isGercekKisi ? '10 haneli, yalnız rakam' : undefined}
              placeholder={isGercekKisi ? 'Tüzel/Muafiyetli/Yabancı seçilince aktif' : ''} />
          </Grid>
        </Section>

        <Section icon="layers" title="Finans" subtitle="Kredi limiti, vade ve cari bilgileri">
          <Grid cols={2}>
            <FieldText form={form} set={set} k="creditLimit1" label="Kredi Limiti 1" num suffix="₺" />
            <FieldText form={form} set={set} k="creditLimit2" label="Kredi Limiti 2" num suffix="₺" />
            <FieldText form={form} set={set} k="dueDate" label="Vade (Gün)" num />
            <FieldSelect form={form} set={set} k="customerCurrencyType" label="Para Birimi" options={currencyOptions} />
            <FieldText form={form} set={set} k="currentBalance" label="Cari Bakiye" num suffix="₺" readOnly span />
          </Grid>
        </Section>
        </>
        )}

        {activeTab === 'belge' && (
        <Section icon="list" title="Belge Ayarları" subtitle="Sevkiyat ve e-belge tercihleri">
          <Grid cols={2}>
            <FieldSelect form={form} set={set} k="defaultShipmentDocumentType" label="Varsayılan Sevkiyat Belge Tipi" options={SHIPMENT_DOC_TYPES} />
            {/* #269 E-Belge mükellefiyeti tek combobox — ayrı E-Fatura/E-İrsaliye kutucukları kaldırıldı
                (mükellefiyet buradan türetilir; alan kalabalığı azaltıldı). */}
            <FieldSelect form={form} set={set} k="eDocumentTaxpayerType" label="E-Belge Mükellef Tipi" options={EDOCUMENT_TYPE_OPTIONS} />
            {checkRow('isPublicInstitution', 'Kamu Kurumu')}
          </Grid>
        </Section>
        )}

        {/* #744 Müşteri özelleştirilmiş alan değerleri. "Listeden Seçim" (5) tipinde tanımlı
            seçeneklerden biri seçilir; değer olarak KOD saklanır. */}
        {activeTab === 'ekbilgiler' && (
        <Section icon="info" title="Ek Bilgiler" subtitle="Müşteri özelleştirilmiş alanları">
          {isNew ? (
            <div className="dps-empty">
              <Icon name="info" size={34} />
              <p>Özel alan değerleri için önce müşteriyi kaydedin</p>
              <span>Müşteri oluşturulduktan sonra bu sekmeden değerleri girebilirsiniz.</span>
            </div>
          ) : custFieldDefs.length === 0 ? (
            <div className="dps-empty">
              <Icon name="info" size={34} />
              <p>Tanımlı özelleştirilmiş alan yok</p>
              <span>Müşteri Özelleştirilmiş Alan ekranından tanımlayabilirsiniz.</span>
            </div>
          ) : (
            <Grid cols={2}>
              {custFieldDefs.map((f) => {
                const fid = pick(f, 'id', 'Id');
                const dt = Number(pick(f, 'dataType', 'DataType')) || 1;
                const req = !!pick(f, 'isRequired', 'IsRequired');
                const name = pick(f, 'name', 'Name') ?? '';
                const val = custFieldValues[fid] ?? '';
                const onVal = (v) => setCustFieldValues((p) => ({ ...p, [fid]: v }));
                let inputEl;
                if (dt === 2) {
                  // Talep 772 — "Sayı" tipli alana harf girilebiliyordu. Native type="number"
                  // tarayıcıya göre e/E/+/- ve hatta düz harf kabul ediyor (Firefox harfi
                  // gösterip değeri sessizce boşaltır). FieldText'teki proje konvansiyonu:
                  // metin girişi + inputMode="decimal" + filtre.
                  inputEl = (
                    <input
                      inputMode="decimal"
                      value={val}
                      onChange={(e) => onVal(sanitizeNumericInput(e.target.value))}
                    />
                  );
                } else if (dt === 3) {
                  inputEl = <input type="date" value={val} onChange={(e) => onVal(e.target.value)} />;
                } else if (dt === 4) {
                  inputEl = (
                    <select value={val} onChange={(e) => onVal(e.target.value)}>
                      <option value="">Seçiniz</option>
                      <option value="Evet">Evet</option>
                      <option value="Hayır">Hayır</option>
                    </select>
                  );
                } else if (dt === 5) {
                  const opts = parseFieldOptions(pick(f, 'options', 'Options'));
                  inputEl = (
                    <select value={val} onChange={(e) => onVal(e.target.value)}>
                      <option value="">Seçiniz</option>
                      {opts.map((o) => <option key={o.code} value={o.code}>{fieldOptionLabel(o)}</option>)}
                    </select>
                  );
                } else {
                  inputEl = <input value={val} onChange={(e) => onVal(e.target.value)} />;
                }
                return (
                  <div className="dps-ff" key={fid}>
                    <div className="dps-ff-lbl">{name}{req && <i>*</i>}</div>
                    <div className="dps-ff-input">{inputEl}</div>
                  </div>
                );
              })}
            </Grid>
          )}
        </Section>
        )}
      </div>

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.title || form.textId}”</strong> müşterisini silmek istediğinize emin misiniz?
          </p>
        </SubModal>
      )}

      {/* #750 Havuz seçim modalı — ürün kartındaki modalın müşteri karşılığı. */}
      {memberModalOpen && (
        <SubModal
          title="Özelleştirilmiş Grup Havuzları"
          maxWidth={520}
          onClose={() => setMemberModalOpen(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setMemberModalOpen(false)} disabled={memberSaving}>İptal</button>
              <button className="dps-btn primary" onClick={applyMemberships} disabled={memberSaving}>{memberSaving ? 'Uygulanıyor…' : 'Uygula'}</button>
            </>
          )}
        >
          <div style={{ marginBottom: 10, fontSize: 12.5, color: 'var(--dps-ink-3, #64748b)' }}>
            Müşterinin ekleneceği havuzları işaretleyin. Seçili: {memberDraft.length}
          </div>
          <input
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
            placeholder="Grup kodu / adı ara…"
            style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--dps-line, #d7dde5)', fontSize: 13.5, marginBottom: 10, background: 'var(--dps-field-bg, #fff)' }}
          />
          <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--dps-line, #e5e7eb)', borderRadius: 8 }}>
            {(() => {
              const q = memberSearch.trim().toLocaleLowerCase('tr');
              const list = allDynamicGroups.filter((g) => !q || `${g.textId} ${g.description}`.toLocaleLowerCase('tr').includes(q));
              if (list.length === 0) return <div style={{ padding: 18, textAlign: 'center', fontSize: 13, color: 'var(--dps-ink-3, #94a3b8)' }}>Tanımlı özelleştirilmiş grup yok.</div>;
              return list.map((g) => (
                <label key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 11px', borderBottom: '1px solid var(--dps-line, #f1f5f9)', cursor: 'pointer', fontSize: 13.5 }}>
                  <input type="checkbox" checked={memberDraft.includes(g.id)} onChange={() => toggleMemberDraft(g.id)} />
                  <span style={{ fontWeight: 700, fontFamily: 'ui-monospace, monospace' }}>{g.textId}</span>
                  {g.description && <span style={{ color: 'var(--dps-ink-3, #64748b)' }}>— {g.description}</span>}
                </label>
              ));
            })()}
          </div>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default CustomerEditPage;
